import { ActionIcon, Group, Popover, Select, Stack, Tooltip } from "@mantine/core";
import { InlineDateTimePicker } from "@mantine/dates";
import { useState } from "react";
import { IconCalendarClock, IconClock, IconX } from "../icons";
import { LOCAL_ZONE, wallDate, wallText, zoneClock } from "./zone-clock";

// Imported here rather than beside the core stylesheet so it rides this chunk, as the picker it dresses does.
import "@mantine/dates/styles.css";

// The two icons a box that names an instant carries, whichever way the box itself spells one.
export function InstantPicker({ instant, live, field, zones, onPick, onPin, onClear }: InstantPickerProps) {
  const [picking, setPicking] = useState(false);
  // A blank box is ticking, so the calendar holds the second it opened on rather than move a day from under a click.
  const [openedAt, setOpenedAt] = useState<Date | null>(null);
  // Whose clock the panel is showing and reading, which is nobody's until it is one of the zones still on offer.
  const [chosen, setChosen] = useState<string | null>(null);
  // Two of these in one row is two calendars called the same thing, so a page with a second box names both.
  const of = field ? ` for ${field}` : "";

  const offered = zones?.length ? zones : [LOCAL_ZONE];
  const zone = chosen && offered.includes(chosen) ? chosen : openingZone(offered);
  const showing = live ? openedAt : instant;

  // The panel writes the box rather than a state of its own, so a pick lands as a value somebody could have typed.
  const pickInstant = (wall: string | null) => {
    const date = wall && wallDate(wall, zone);
    if (date) onPick(date, zone);
  };

  // The tick takes what the panel shows; nothing else does, so clicking away leaves a blank box blank.
  const takePick = () => {
    if (live && openedAt) onPick(openedAt, zone);
    setPicking(false);
  };

  return (
    <Group gap={2} wrap="nowrap">
      <Popover opened={picking} onChange={setPicking} position="bottom-end" shadow="md" trapFocus>
        <Tooltip label={`Pick a date and time${of}`} withArrow position="left">
          <Popover.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => {
                setOpenedAt(instant);
                setPicking((open) => !open);
              }}
              aria-label={`Pick a date and time${of}`}
            >
              <IconCalendarClock size="1.1rem" />
            </ActionIcon>
          </Popover.Target>
        </Tooltip>
        <Popover.Dropdown p="xs">
          <Stack gap="xs">
            {/* A zone is what makes the numbers below an instant, so it is asked for above the ones it answers for. */}
            {offered.length > 1 && (
              <Select
                label="Zone"
                data={offered}
                value={zone}
                onChange={setChosen}
                allowDeselect={false}
                // The list belongs to the popover rather than to a portal, a click in one outside it closing both.
                comboboxProps={{ withinPortal: false }}
              />
            )}
            {/* Standalone it stretches to whatever holds it, and a popover is as wide as what is in it. */}
            <InlineDateTimePicker
              withSeconds
              fullWidth={false}
              value={showing && wallText(zoneClock(showing, zone))}
              onChange={pickInstant}
              onSubmit={takePick}
              // Mantine leaves its tick unlabelled, and a button with nothing but a check in it says nothing.
              submitButtonProps={{ "aria-label": "Use this time" }}
            />
          </Stack>
        </Popover.Dropdown>
      </Popover>
      {live
        ? (
          <Tooltip label={`Pin the current time${of}`} withArrow position="left">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onPin}
              aria-label={`Pin the current time${of}`}
            >
              <IconClock size="1.1rem" />
            </ActionIcon>
          </Tooltip>
        )
        : (
          <Tooltip label={`Follow the clock${of}`} withArrow position="left">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onClear}
              aria-label={`Follow the clock${of}`}
            >
              <IconX size="1.1rem" />
            </ActionIcon>
          </Tooltip>
        )}
    </Group>
  );
}

// A wall clock is usually typed on the clock of whoever is typing it, and on the first one listed where that is none.
function openingZone(offered: string[]): string {
  return offered.includes(LOCAL_ZONE) ? LOCAL_ZONE : offered[0];
}

export interface InstantPickerProps {
  // What the calendar opens on, and null where what the box says cannot be read as a time at all.
  instant: Date | null;
  live: boolean;
  // What the box beside it is called, where a page has more than one of them and the labels would otherwise match.
  field?: string;
  // The zones the page has the time of, which are the ones there is any point picking in; a page with none says so.
  zones?: string[];
  // The zone comes with the instant, since a page spelling a wall clock has to say whose it was.
  onPick: (instant: Date, timeZone: string) => void;
  onPin: () => void;
  onClear: () => void;
}

// What a box holding these is sized to: two icons, where a right section is written for one.
export const INSTANT_PICKER_WIDTH = 64;
